/**
 * /emails/historico — Lista append-only de emails disparados (M11.3).
 *
 * Filtros: template, situacao, destinatario.
 * Permissao: email:view.
 */
import React, { useCallback, useEffect, useState } from "react";
import { plural } from "../../../utils/dinheiro";
import { FiInbox } from "react-icons/fi";
import styled from "styled-components";

import EntityHeader from "../../../components/EntityHeader";
import { useAuth } from "../../../context/AuthContext";
import { PageContainer } from "../../../styles/shared";

import {
  Card, EmptyMsg, ErrorMsg, FilterBar, Pill, SearchInput, SectionTitle, Select,
  Table,
} from "../../Orcamentos/styles";

import { SITUACOES_EMAIL, listarEmailsEnviados } from "../../../services/emails";


export default function HistoricoEmails() {
  const { request } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filtroTemplate, setFiltroTemplate] = useState("");
  const [filtroSituacao, setFiltroSituacao] = useState("");
  const [busca, setBusca] = useState("");

  const carregar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = await listarEmailsEnviados(request, {
        template_id: filtroTemplate || undefined,
        situacao: filtroSituacao || undefined,
        destinatario: busca || undefined,
      });
      setItems(Array.isArray(p) ? p : (p?.results || []));
    } catch (err) {
      setError(err?.message || "Falha ao carregar histórico.");
    } finally {
      setLoading(false);
    }
  }, [request, filtroTemplate, filtroSituacao, busca]);

  useEffect(() => { carregar(); }, [carregar]);

  return (
    <PageContainer>
      <EntityHeader
        title="Histórico de E-mails"
        subtitle={plural(items.length, "e-mail enviado", "e-mails enviados")}
        icone={FiInbox}
      />

      <Card>
        <SectionTitle>Filtros</SectionTitle>
        <FilterBar>
          <SearchInput
            placeholder="Buscar destinatario..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          <Select value={filtroTemplate} onChange={(e) => setFiltroTemplate(e.target.value)}>
            <option value="">Todos templates</option>
            <option value="orcamento_enviado">Orçamento enviado</option>
            <option value="contrato_assinado">Contrato assinado</option>
            <option value="nfe_emitida">NF-e emitida</option>
            <option value="boleto_proximo_vencimento">Boleto vencimento</option>
            <option value="assistencia_aberta">Assistencia aberta</option>
            <option value="_teste_inline">Teste inline</option>
          </Select>
          <Select value={filtroSituacao} onChange={(e) => setFiltroSituacao(e.target.value)}>
            <option value="">Todas situações</option>
            {Object.entries(SITUACOES_EMAIL).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </Select>
        </FilterBar>
      </Card>

      {error && <ErrorMsg>{error}</ErrorMsg>}
      {loading && <EmptyMsg>Carregando...</EmptyMsg>}

      {!loading && items.length === 0 && (
        <EmptyMsg>Nenhum email no histórico ainda.</EmptyMsg>
      )}

      {!loading && items.length > 0 && (
        <Card>
          <ScrollWrap>
            <Table>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Template</th>
                  <th>Destinatario</th>
                  <th>Assunto</th>
                  <th>Provider</th>
                  <th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {items.map((m) => {
                  const sit = SITUACOES_EMAIL[m.situacao] || { label: m.situacao, tone: "neutral" };
                  return (
                    <tr key={m.id}>
                      <td>{new Date(m.enviado_em).toLocaleString("pt-BR")}</td>
                      <td><code style={{ fontSize: "0.78rem" }}>{m.template_id}</code></td>
                      <td>{m.destinatario}</td>
                      <td title={m.assunto}>{m.assunto}</td>
                      <td>{m.provider_usado}</td>
                      <td>
                        <Pill $tone={sit.tone}>{sit.label}</Pill>
                        {m.erro_detalhe && (
                          <ErroDetalhe title={m.erro_detalhe}>(erro)</ErroDetalhe>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </ScrollWrap>
        </Card>
      )}
    </PageContainer>
  );
}


const ScrollWrap = styled.div`
  overflow-x: hidden;
  table { table-layout: fixed; width: 100%; }
`;

const ErroDetalhe = styled.span`
  display: block;
  font-size: 0.7rem;
  color: ${({ theme }) => theme.dangerText};
  margin-top: 2px;
`;
